import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Opens the Rebuy Smart Cart — but only if it has already finished loading.
 *
 * We intentionally never wait/poll for Rebuy here. Blocking on a third-party app
 * was what made the slide cart feel slow to open (up to a 1s stall). Instead:
 * if Rebuy is already warm we use it (for its upsells); otherwise the caller
 * falls back to the instant, server-rendered native drawer. Rebuy boots in the
 * background, so it becomes the cart that opens as soon as it is ready.
 *
 * @returns {boolean} true if the Rebuy Smart Cart was opened, false otherwise.
 */
function tryOpenRebuy() {
  if (typeof window.Rebuy !== 'undefined' && window.Rebuy.SmartCart?.status === 'ready') {
    window.Rebuy.SmartCart.show();
    return true;
  }

  return false;
}

/**
 * A custom element that manages a cart drawer.
 *
 * Opens the Rebuy Smart Cart when available; falls back to the native
 * drawer if Rebuy is not installed or not yet initialised.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * Called when a product is added to the cart.
   * Respects the auto-open attribute and prefers Rebuy when it is already warm.
   */
  #handleCartAdd = () => {
    if (!this.hasAttribute('auto-open')) return;

    // Open instantly: use Rebuy if it's ready, otherwise the native drawer now.
    if (!tryOpenRebuy()) {
      this.#openNative();
    }
  };

  /**
   * Called by the on:click="/open" declarative handler on the cart button.
   * Opens instantly: prefers Rebuy when warm, otherwise the native drawer.
   */
  open() {
    if (!tryOpenRebuy()) {
      this.#openNative();
    }
  }

  /**
   * Opens the native cart drawer and wires up the installments CTA close behaviour.
   */
  #openNative() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}


function initCartUpsellSlider() {
  new Swiper('[js-cart-upsell-slider]', {
    slidesPerView: 2.2,
    spaceBetween: 18,
    loop: false,
    freeMode: true,
    mousewheel: {
      forceToAxis: true, 
      sensitivity: 1, 
    },
    breakpoints: {
      0: {
        slidesPerView: 1.76,
          spaceBetween: 13,
      },
      768: {
        slidesPerView: 2.26,
        spaceBetween: 18,
      }
    }
  });
}

initCartUpsellSlider();
document.addEventListener("cart-upsell-slider:init", initCartUpsellSlider);